import React from 'react';
import { FiTrendingUp, FiTrendingDown, FiCheckCircle, FiStar, FiAlertCircle, FiAward } from 'react-icons/fi';
import PremiumGate from '../shared/PremiumGate';
import './PersonalizedInsights.css';

const PersonalizedInsights = ({ isPremium = false, analyticsData = {} }) => {
    // Extract data
    const stepsMetDays = analyticsData.dailyActivityAnalytics?.daysMetStepsGoal || 0;

    const avgWaterMl = analyticsData.waterIntakeAnalytics?.avgDailyIntake || 0;
    const targetWaterMl = analyticsData.waterIntakeAnalytics?.targetDailyIntake || 2000;
    
    const avgSleep = analyticsData.sleepAnalytics?.avgSleepDuration || 0;
    const sleepConsistency = analyticsData.sleepAnalytics?.sleepConsistency || 'Fair';

    // Calculate comparative metrics
    const waterDiffPercent = targetWaterMl > 0 ? Math.round(((avgWaterMl - targetWaterMl) / targetWaterMl) * 100) : 0;
    const sleepDiffPercent = 8.0 > 0 ? Math.round(((avgSleep - 8.0) / 8.0) * 100) : 0;

    // Premium insights generated by backend if premium, otherwise use fallback rule-based preview cards
    const backendInsights = analyticsData.premiumInsights || [];

    return (
        <PremiumGate
            isPremium={isPremium}
            featureName="Neural Core Insights & Trends"
            featureDesc="Unlock comparative logs, weekly delta percentages, and metabolic velocity calculations."
        >
            <div className="personalized-insights-container">
                <div className="insights-header">
                    <FiStar className="star-icon" />
                    <h3>Neural Core Insights</h3>
                    <span className="premium-badge">PREMIUM</span>
                </div>

                <div className="insights-cards-grid">
                    {/* Hydration Card */}
                    <div className="insight-card">
                        <div className="card-top">
                            <span className="card-label">Hydration Velocity</span>
                            {waterDiffPercent >= 0 ? (
                                <span className="delta-badge positive">
                                    <FiTrendingUp /> +{waterDiffPercent}% vs target
                                </span>
                            ) : (
                                <span className="delta-badge negative">
                                    <FiTrendingDown /> {waterDiffPercent}% vs target
                                </span>
                            )}
                        </div>
                        <p className="insight-text">
                            {waterDiffPercent >= 0 
                                ? `Your average daily water intake is ${Math.round(avgWaterMl)}ml, exceeding your hydration target. Excellent focus on metabolic cell hydration.`
                                : `Your average hydration of ${Math.round(avgWaterMl)}ml is below target. Increasing intake by 250ml/day will improve muscle recovery times.`
                            }
                        </p>
                    </div>

                    {/* Sleep Card */}
                    <div className="insight-card">
                        <div className="card-top">
                            <span className="card-label">Sleep Standard Deviation</span>
                            {sleepDiffPercent >= -10 ? (
                                <span className="delta-badge positive">
                                    <FiCheckCircle /> Consistency: {sleepConsistency}
                                </span>
                            ) : (
                                <span className="delta-badge warning">
                                    <FiAlertCircle /> Sleep debt warning
                                </span>
                            )}
                        </div>
                        <p className="insight-text">
                            {avgSleep > 0 
                                ? `Sleeping ${avgSleep.toFixed(1)} hours on average with a '${sleepConsistency}' consistency rating. Maintain this window for optimal neural restoration.`
                                : "No sleep logs registered this week. Begin logging sleep to compute circadian recovery indices."
                            }
                        </p>
                    </div>

                    {/* Activity Card */}
                    <div className="insight-card">
                        <div className="card-top">
                            <span className="card-label">Steps Consistency</span>
                            <span className="delta-badge positive">
                                <FiAward /> {stepsMetDays}/7 Days Completed
                            </span>
                        </div>
                        <p className="insight-text">
                            {stepsMetDays > 4
                                ? `Fantastic step consistency! You met your target steps on ${stepsMetDays} out of the last 7 days, maintaining strong daily circulation.`
                                : `You hit your daily steps goal on ${stepsMetDays} out of 7 days. Try taking a short 10-minute walk after meals to increase consistency.`
                            }
                        </p>
                    </div>
                </div>

                {backendInsights.length > 0 && (
                    <div className="backend-insights-section">
                        <h4>🎙️ AI Personalized Recommendations</h4>
                        <ul className="backend-insights-list">
                            {backendInsights.map((insight, idx) => (
                                <li key={idx} className="backend-insight-item">
                                    <span className="insight-bullet">✦</span>
                                    <span>{insight}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </PremiumGate>
    );
};

export default PersonalizedInsights;
